import React, { useRef, useEffect, useCallback } from 'react';

const TextElement = ({
  shape,
  isSelected,
  isEditing,
  pan,
  tool,
  fadedShapes,
  onSelect,
  onEditStart,
  onEditEnd,
  onTextChange,
  onResize,
  getPointerCoords,
  svgRef,
  panRef,
  onShapeUpdate,
}) => {
  const textEditRef = useRef(null);
  // Keeps last known typed text so commitAndExit can save it even if textEditRef was detached
  const lastTextRef = useRef('');
  // Persistent off-screen div for height measurement during width resize
  const measureDivRef = useRef(null);

  const w = Math.max(50, shape.width || 200);
  const h = Math.max(40, shape.height || 40);
  const faded = fadedShapes?.has(shape.id);

  // --- Measurement div (one per instance, cleaned up on unmount) ---
  const getMeasureDiv = useCallback(() => {
    if (!measureDivRef.current) {
      const div = document.createElement('div');
      div.style.cssText =
        'position:absolute;visibility:hidden;top:-9999px;left:-9999px;' +
        'white-space:pre-wrap;word-break:break-word;overflow-wrap:break-word;' +
        'line-height:1.2;padding:4px;box-sizing:border-box;';
      document.body.appendChild(div);
      measureDivRef.current = div;
    }
    return measureDivRef.current;
  }, []);

  useEffect(() => {
    return () => {
      if (measureDivRef.current) {
        document.body.removeChild(measureDivRef.current);
        measureDivRef.current = null;
      }
    };
  }, []);

  const measureHeight = useCallback((text, width) => {
    const div = getMeasureDiv();
    div.style.fontSize = `${shape.fontSize || 24}px`;
    div.style.fontFamily = shape.font || 'Arial, sans-serif';
    div.style.fontWeight = shape.bold ? 'bold' : 'normal';
    div.style.fontStyle = shape.italic ? 'italic' : 'normal';
    div.style.textDecoration = shape.underline ? 'underline' : 'none';
    div.style.textAlign = shape.textAlign || 'left';
    div.style.width = `${width}px`;
    div.textContent = text;
    return Math.max(40, div.scrollHeight);
  }, [shape.fontSize, shape.font, shape.bold, shape.italic, shape.underline, shape.textAlign, getMeasureDiv]);

  // --- Auto-height from contentEditable scrollHeight ---
  const syncHeight = useCallback(() => {
    const el = textEditRef.current;
    if (!el) return;
    const newH = Math.max(40, el.scrollHeight);
    if (Math.abs(newH - shape.height) > 2) {
      onResize(shape.id, { height: newH });
    }
  }, [shape.id, shape.height, onResize]);

  // --- Focus + cursor at end when entering edit mode ---
  useEffect(() => {
    if (!isEditing || !textEditRef.current) return;
    const el = textEditRef.current;
    // Set initial content once (avoid React reconciliation conflicts during typing)
    el.innerText = shape.text || '';
    lastTextRef.current = shape.text || '';
    el.focus();
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false); // cursor at end
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
    syncHeight();
  // shape.text intentionally omitted — set content only on edit start
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  // Re-sync height when formatting changes from toolbar while editing
  useEffect(() => {
    if (isEditing) syncHeight();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shape.fontSize, shape.bold, shape.italic, shape.underline, shape.textAlign, shape.font]);

  // --- Drag ---
  const handlePointerDown = useCallback((e) => {
    if (tool === 'pen' || tool === 'highlighter' || tool === 'eraser') return;
    if (isEditing) return;
    if (e.target.dataset.resizeHandle) return;

    e.stopPropagation();
    onSelect(shape.id);

    const { x: bx, y: by } = getPointerCoords(e, panRef.current, svgRef);
    const offset = { x: bx - shape.x, y: by - shape.y };

    const onMove = (ev) => {
      const { x, y } = getPointerCoords(ev, panRef.current, svgRef);
      onShapeUpdate(shape.id, { x: x - offset.x, y: y - offset.y });
    };
    const onUp = () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }, [shape.id, shape.x, shape.y, isEditing, tool, onSelect, onShapeUpdate, getPointerCoords, panRef, svgRef]);

  // --- Click: select on first click, enter edit on second ---
  const handleClick = useCallback((e) => {
    if (tool === 'pen' || tool === 'highlighter' || tool === 'eraser') return;
    e.stopPropagation();
    if (!isSelected) { onSelect(shape.id); return; }
    if (!isEditing) onEditStart(shape.id);
  }, [shape.id, isSelected, isEditing, tool, onSelect, onEditStart]);

  const handleDoubleClick = useCallback((e) => {
    if (tool === 'pen' || tool === 'highlighter' || tool === 'eraser') return;
    e.stopPropagation();
    onSelect(shape.id);
    onEditStart(shape.id);
  }, [shape.id, tool, onSelect, onEditStart]);

  // --- Commit text and exit edit ---
  const commitAndExit = useCallback(() => {
    const text = textEditRef.current ? textEditRef.current.innerText : lastTextRef.current;
    onTextChange(shape.id, text || '');
    if (textEditRef.current) syncHeight();
    onEditEnd(shape.id);
  }, [shape.id, onTextChange, onEditEnd, syncHeight]);

  const handleBlur = useCallback(() => {
    commitAndExit();
  }, [commitAndExit]);

  // --- Keyboard ---
  const handleKeyDown = useCallback((e) => {
    // Always prevent whiteboard shortcuts from stealing these keys
    if (e.key === 'Delete' || e.key === 'Backspace' || e.code === 'Space') {
      e.stopPropagation();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      commitAndExit();
      return;
    }
    // Enter = natural newline via contentEditable; just block propagation
    if (e.key === 'Enter') e.stopPropagation();
  }, [commitAndExit]);

  // --- Input: track latest text + auto-resize height ---
  const handleInput = useCallback(() => {
    if (textEditRef.current) lastTextRef.current = textEditRef.current.innerText || '';
    syncHeight();
  }, [syncHeight]);

  // --- Paste: plain text only, inserted at cursor position ---
  const handlePaste = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const text = e.clipboardData.getData('text/plain');
    const sel = window.getSelection();
    if (sel && sel.rangeCount) {
      const range = sel.getRangeAt(0);
      range.deleteContents();
      range.insertNode(document.createTextNode(text));
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }, []);

  // --- Width resize (left / right side handles) ---
  const handleWidthResizeStart = useCallback((e, side) => {
    e.stopPropagation();
    e.preventDefault();
    const svgRect = svgRef.current.getBoundingClientRect();
    const toBoard = (clientX) => (clientX - svgRect.left - panRef.current.x) / panRef.current.scale;
    const startBoardX = toBoard(e.clientX);
    const startW = shape.width || 200;
    const startShapeX = shape.x;
    const text = shape.text || '';

    const onMove = (ev) => {
      const dx = toBoard(ev.clientX) - startBoardX;
      let newW, newX;
      if (side === 'right') {
        newW = Math.max(50, startW + dx);
        newX = startShapeX;
      } else {
        newW = Math.max(50, startW - dx);
        newX = startShapeX + (startW - newW);
      }
      const newH = measureHeight(text, newW);
      onResize(shape.id, { width: newW, height: newH, x: newX });
    };
    const onUp = () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }, [shape.id, shape.x, shape.width, shape.text, onResize, svgRef, panRef, measureHeight]);

  // --- Corner resize: scales font size ---
  const handleCornerResizeStart = useCallback((e, corner) => {
    e.stopPropagation();
    e.preventDefault();
    const startClientX = e.clientX;
    const startClientY = e.clientY;
    const startFontSize = shape.fontSize || 24;
    const startW = shape.width || 200;
    const startH = shape.height || 40;
    const startX = shape.x;
    const startY = shape.y;

    const onMove = (ev) => {
      const dx = ev.clientX - startClientX;
      const dy = ev.clientY - startClientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      // Grow when dragging away from center of the box
      const grow =
        (corner[0] === 1 && corner[1] === 1 && (dx > 0 || dy > 0)) ||
        (corner[0] === 1 && corner[1] === 0 && (dx > 0 || dy < 0)) ||
        (corner[0] === 0 && corner[1] === 1 && (dx < 0 || dy > 0)) ||
        (corner[0] === 0 && corner[1] === 0 && (dx < 0 || dy < 0));

      const newFontSize = Math.max(8, Math.min(200,
        grow ? startFontSize + dist / 15 : startFontSize - dist / 15
      ));
      const ratio = newFontSize / startFontSize;
      const newW = Math.max(50, startW * ratio);
      const newH = Math.max(40, startH * ratio);
      // Anchor opposite corner
      const newX = corner[0] === 0 ? startX + (startW - newW) : startX;
      const newY = corner[1] === 0 ? startY + (startH - newH) : startY;

      onResize(shape.id, { width: newW, height: newH, x: newX, y: newY, fontSize: Math.round(newFontSize) });
    };
    const onUp = () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }, [shape, onResize]);

  const CORNERS = [[0,0],[1,0],[0,1],[1,1]];
  const CORNER_CURSORS = ['nw-resize','ne-resize','sw-resize','se-resize'];

  return (
    <g>
      <foreignObject x={shape.x} y={shape.y} width={w} height={h} style={{ overflow: 'visible' }}>
        <div
          ref={textEditRef}
          data-shape-id={shape.id}
          contentEditable={isEditing}
          suppressContentEditableWarning
          style={{
            width: w,
            minHeight: 40,
            background: shape.highlight || 'none',
            border: 'none',
            padding: '4px',
            color: shape.color || '#222',
            fontWeight: shape.bold ? 'bold' : 'normal',
            fontStyle: shape.italic ? 'italic' : 'normal',
            textDecoration: [shape.underline && 'underline', shape.strikethrough && 'line-through'].filter(Boolean).join(' ') || 'none',
            textAlign: shape.textAlign || 'left',
            fontSize: shape.fontSize || 24,
            fontFamily: shape.font || 'Arial, sans-serif',
            lineHeight: 1.2,
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            whiteSpace: 'pre-wrap',
            overflow: 'visible',
            outline: 'none',
            cursor: isEditing ? 'text' : 'pointer',
            userSelect: isEditing ? 'text' : 'none',
            WebkitUserSelect: isEditing ? 'text' : 'none',
            opacity: faded ? 0.3 : 1,
            pointerEvents: 'auto',
            boxSizing: 'border-box',
          }}
          onPointerDown={handlePointerDown}
          onClick={handleClick}
          onDoubleClick={handleDoubleClick}
          onBlur={isEditing ? handleBlur : undefined}
          onKeyDown={isEditing ? handleKeyDown : undefined}
          onInput={isEditing ? handleInput : undefined}
          onPaste={isEditing ? handlePaste : undefined}
        >
          {/* Only render text when NOT editing — during edit, content is managed by the DOM */}
          {!isEditing && shape.text}
        </div>
      </foreignObject>

      {isSelected && (
        <g style={{ pointerEvents: 'auto' }}>
          <rect
            x={shape.x - 8} y={shape.y - 8}
            width={w + 16} height={h + 16}
            fill="none" stroke="#3b82f6" strokeWidth={1}
            style={{ pointerEvents: 'none' }}
          />
          {CORNERS.map((corner, i) => (
            <circle
              key={i}
              cx={shape.x - 8 + corner[0] * (w + 16)}
              cy={shape.y - 8 + corner[1] * (h + 16)}
              r={8 / pan.scale}
              fill="#fff" stroke="#3b82f6" strokeWidth={2 / pan.scale}
              data-resize-handle="true"
              style={{ cursor: CORNER_CURSORS[i], pointerEvents: 'auto' }}
              onPointerDown={e => handleCornerResizeStart(e, corner)}
            />
          ))}
          {[['left', 0, 0.5], ['right', 1, 0.5]].map(([side, hx, hy]) => (
            <rect
              key={side}
              x={shape.x - 8 + hx * (w + 16) - 6 / pan.scale}
              y={shape.y - 8 + hy * (h + 16) - 6 / pan.scale}
              width={12 / pan.scale} height={12 / pan.scale}
              fill="#3b82f6" stroke="#fff" strokeWidth={1 / pan.scale}
              data-resize-handle="true"
              style={{ cursor: `${side === 'left' ? 'w' : 'e'}-resize`, pointerEvents: 'auto' }}
              onPointerDown={e => handleWidthResizeStart(e, side)}
            />
          ))}
        </g>
      )}
    </g>
  );
};

export default TextElement;
